import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";

const SAVE_UPDATES_LABEL = "Save Updates";
const SEND_TO_REVIEWER_LABEL = "Send to Reviewer";
const SEND_TO_REVIEWERS_LABEL = "Send to Reviewers";
const UPDATE_REVIEWER_LABEL = "Update Reviewer";
const UPDATE_REVIEWERS_LABEL = "Update Reviewers";
const REMOVE_REVIEWER_LABEL = "Remove Reviewer";
const REMOVE_REVIEWERS_LABEL = "Remove Reviewers";
const REVOKE_EDIT_RIGHTS_LABEL = "Revoke Edit Rights";
const NO_UPDATES_LABEL = "No Updates to Save";
const SEND_READY_DESCRIPTION = "Notifies the reviewers you just added.";
const SEND_CONFIRMS_UNSAVED_CHANGES_DESCRIPTION =
  "Asks to save your unsaved proposal changes first, then notifies the reviewers you just added.";
const SEND_NEEDS_ADDED_REVIEWER_DESCRIPTION = "Add a reviewer to send this proposal for review.";

const CONFIRM_OK_BUTTON_TEXT_BY_LABEL = new Map([
  [SAVE_UPDATES_LABEL, "Save"],
  [SEND_TO_REVIEWER_LABEL, "Send"],
  [SEND_TO_REVIEWERS_LABEL, "Send"],
  [REMOVE_REVIEWER_LABEL, "Remove"],
  [REMOVE_REVIEWERS_LABEL, "Remove"],
  [REVOKE_EDIT_RIGHTS_LABEL, "Revoke"],
]);

export type SendButtonLabelArgs = {
  addedReviewersCount?: number;
  awaitingRequestCount?: number;
  changedReviewersCount?: number;
  editRightsGrantedCount?: number;
  editRightsRevokedCount?: number;
  removedReviewersCount?: number;
  selectedReviewersCount?: number;
  updatedReviewersCount?: number;
  proposalIsModifiedByBuilder: boolean;
  proposalHasUnsavedRedlineChanges: boolean;
};

export type ReviewerChangeSummary = {
  addedEditingReviewersCount: number;
  addedReviewersCount: number;
  /**
   * Reviewers already on the roster whom nobody has asked yet -- the ones a revision of a hired
   * agreement carried over from the agreement it revises. They are not an edit to the roster, so the
   * before/after diff above cannot see them, and without counting them the Send control read "No
   * updates" over a team that had never been told the revision existed.
   */
  awaitingRequestCount: number;
  changedReviewersCount: number;
  editRightsGrantedCount: number;
  editRightsRevokedCount: number;
  removedReviewersCount: number;
  selectedReviewersCount: number;
  updatedReviewersCount: number;
  hasReviewerChanges: boolean;
};

export type ReviewerActionStateArgs = {
  originalReviewers?: ProposalReviewer[];
  selectedReviewers?: ProposalReviewer[];
  proposalIsModifiedByBuilder: boolean;
  proposalHasUnsavedRedlineChanges: boolean;
  reviewersAreModified?: boolean;
};

export type ReviewerSendActionStateArgs = {
  originalReviewers?: ProposalReviewer[];
  selectedReviewers?: ProposalReviewer[];
  /** The host page's unsaved-changes signal (`isDirty` on both proposal pages). */
  proposalHasUnsavedChanges: boolean;
};

export type ReviewerSendActionState = {
  addedReviewersCount: number;
  /**
   * Whether pressing Send has unsaved proposal content riding along with it, and so must ask before it
   * goes (#5134). Send itself is available either way — this only decides whether a confirmation stands
   * between the press and the save.
   */
  confirmsUnsavedProposalChanges: boolean;
  /** Why the control is in the state it is in — rendered beside it and named as its accessible description. */
  description: string;
  disabled: boolean;
  label: string;
};

export type ReviewerActionState = ReviewerChangeSummary & {
  label: string;
  disabled: boolean;
  hasPendingSaveUpdates: boolean;
  confirmTitle: string;
  confirmMessage: string;
  confirmSupplementalMessage?: string;
  confirmOkButtonText: string;
};

type ReviewerSelectionArgs = {
  selectedReviewers: ProposalReviewer[];
  reviewer: ProposalReviewer;
};

type ReviewerChange = {
  originalReviewer: ProposalReviewer;
  reviewer: ProposalReviewer;
};

function getSelectedReviewer(
  selectedReviewers: ProposalReviewer[],
  reviewer: ProposalReviewer,
): ProposalReviewer | undefined {
  return selectedReviewers.find((currentReviewer) => currentReviewer.userId.isEqualTo(reviewer.userId));
}

export function toggleReviewerSelection(args: ReviewerSelectionArgs): ProposalReviewer[] {
  const { selectedReviewers, reviewer } = args;
  const selectedReviewer = getSelectedReviewer(selectedReviewers, reviewer);

  if (selectedReviewer) {
    return selectedReviewers.filter((currentReviewer) => !currentReviewer.userId.isEqualTo(reviewer.userId));
  }

  return [...selectedReviewers, reviewer];
}

/**
 * Drops a reviewer from the selection outright (#5134's trash can). Not `toggleReviewerSelection`:
 * a toggle pressed against somebody who is already gone silently re-adds them, which is the opposite
 * of what a delete control promises. Removing somebody who is not selected is a no-op.
 */
export function removeReviewer(args: ReviewerSelectionArgs): ProposalReviewer[] {
  const { selectedReviewers, reviewer } = args;

  return selectedReviewers.filter((currentReviewer) => !currentReviewer.userId.isEqualTo(reviewer.userId));
}

/**
 * Active reviewers first, everyone else after, each group in the order it arrived (#5134). The list the
 * popover renders is the coworker directory with the roster folded into it, so without this the people
 * actually on the proposal sit wherever the alphabet put them — below a scroll, on a long team.
 */
export function orderReviewersForDisplay(args: {
  reviewers: ProposalReviewer[];
  activeReviewerUserIds: ReadonlySet<string>;
}): ProposalReviewer[] {
  const { reviewers, activeReviewerUserIds } = args;
  const activeReviewers = reviewers.filter((reviewer) => activeReviewerUserIds.has(reviewer.userId.value));
  const inactiveReviewers = reviewers.filter((reviewer) => !activeReviewerUserIds.has(reviewer.userId.value));

  return [...activeReviewers, ...inactiveReviewers];
}

export function toggleReviewerEditing(args: ReviewerSelectionArgs): ProposalReviewer[] {
  const { selectedReviewers, reviewer } = args;
  const selectedReviewer = getSelectedReviewer(selectedReviewers, reviewer);

  if (!selectedReviewer) {
    const updatedReviewers = [
      ...selectedReviewers,
      new ProposalReviewer({
        entityMember: reviewer.entityMember,
        isEditing: true,
        dateApproved: reviewer.dateApproved,
        customMessage: reviewer.customMessage,
      }),
    ];

    return updatedReviewers;
  }

  const updatedReviewers = selectedReviewers.map((currentReviewer) => {
    if (!currentReviewer.userId.isEqualTo(reviewer.userId)) {
      return currentReviewer;
    }

    return new ProposalReviewer({
      entityMember: selectedReviewer.entityMember,
      isEditing: !selectedReviewer.isEditing,
      dateApproved: selectedReviewer.dateApproved,
      customMessage: selectedReviewer.customMessage,
    });
  });

  return updatedReviewers;
}

export function getReviewerChangeSummary(args: {
  originalReviewers?: ProposalReviewer[];
  selectedReviewers?: ProposalReviewer[];
}): ReviewerChangeSummary {
  const originalReviewers = args.originalReviewers ?? [];
  const selectedReviewers = args.selectedReviewers ?? [];
  const originalReviewersByUserId = getReviewersByUserId(originalReviewers);
  const selectedReviewersByUserId = getReviewersByUserId(selectedReviewers);
  const addedReviewers = selectedReviewers.filter(
    (reviewer) => !originalReviewersByUserId.has(reviewer.userId.value),
  );
  const addedReviewersCount = addedReviewers.length;
  const addedEditingReviewersCount = addedReviewers.filter((reviewer) => reviewer.isEditing).length;
  const removedReviewersCount = originalReviewers.filter(
    (originalReviewer) => !selectedReviewersByUserId.has(originalReviewer.userId.value),
  ).length;
  const changedReviewers = selectedReviewers
    .map((reviewer) => ({
      reviewer,
      originalReviewer: originalReviewersByUserId.get(reviewer.userId.value),
    }))
    .filter(
      (reviewerChange): reviewerChange is ReviewerChange =>
        Boolean(reviewerChange.originalReviewer && !reviewerChange.originalReviewer.isEqualTo(reviewerChange.reviewer)),
    );
  const editRightsGrantedCount = changedReviewers.filter(
    ({ originalReviewer, reviewer }) => !originalReviewer.isEditing && reviewer.isEditing,
  ).length;
  const editRightsRevokedCount = changedReviewers.filter(
    ({ originalReviewer, reviewer }) => originalReviewer.isEditing && !reviewer.isEditing,
  ).length;
  const updatedReviewersCount = changedReviewers.filter(({ originalReviewer, reviewer }) => {
    return (
      !originalReviewer.entityMember.isEqualTo(reviewer.entityMember) ||
      (originalReviewer.customMessage ?? "") !== (reviewer.customMessage ?? "")
    );
  }).length;
  const awaitingRequestCount = selectedReviewers.filter(
    (reviewer) => reviewer.isAwaitingReviewRequest && originalReviewersByUserId.has(reviewer.userId.value),
  ).length;

  return {
    addedEditingReviewersCount,
    addedReviewersCount,
    awaitingRequestCount,
    changedReviewersCount: changedReviewers.length,
    editRightsGrantedCount,
    editRightsRevokedCount,
    removedReviewersCount,
    selectedReviewersCount: selectedReviewers.length,
    updatedReviewersCount,
    hasReviewerChanges:
      addedReviewersCount > 0 ||
      removedReviewersCount > 0 ||
      changedReviewers.length > 0 ||
      awaitingRequestCount > 0,
  };
}

function getReviewersByUserId(reviewers: ProposalReviewer[]): Map<string, ProposalReviewer> {
  const reviewersByUserId = new Map<string, ProposalReviewer>();

  reviewers.forEach((reviewer) => {
    if (!reviewersByUserId.has(reviewer.userId.value)) {
      reviewersByUserId.set(reviewer.userId.value, reviewer);
    }
  });

  return reviewersByUserId;
}

export function getSendButtonLabel(args: SendButtonLabelArgs): string {
  const {
    addedReviewersCount = 0,
    awaitingRequestCount = 0,
    changedReviewersCount = 0,
    editRightsGrantedCount = 0,
    editRightsRevokedCount = 0,
    removedReviewersCount = 0,
    selectedReviewersCount = 0,
    updatedReviewersCount = 0,
    proposalIsModifiedByBuilder,
    proposalHasUnsavedRedlineChanges,
  } = args;
  // Comments used to count as pending work here, so a reviewer holding one saw a "Save Updates" button
  // over a proposal they had not touched. A comment is written when it is written; the reviewer's button
  // speaks for their proposal changes.
  const hasPendingChanges = proposalIsModifiedByBuilder || proposalHasUnsavedRedlineChanges;
  const hasReviewerChanges =
    addedReviewersCount > 0 || removedReviewersCount > 0 || changedReviewersCount > 0 || awaitingRequestCount > 0;
  const hasSendToReviewerChanges =
    addedReviewersCount > 0 || awaitingRequestCount > 0 || editRightsGrantedCount > 0;
  const hasRemovalChanges = removedReviewersCount > 0;
  const hasRevokeEditRightsChanges = editRightsRevokedCount > 0;
  const hasUnclassifiedReviewerChanges =
    changedReviewersCount > 0 &&
    editRightsGrantedCount === 0 &&
    editRightsRevokedCount === 0 &&
    updatedReviewersCount === 0;
  const hasReviewerUpdates = updatedReviewersCount > 0 || hasUnclassifiedReviewerChanges;
  const reviewerChangeTypesCount = [
    hasSendToReviewerChanges,
    hasRemovalChanges,
    hasRevokeEditRightsChanges,
    hasReviewerUpdates,
  ].filter(Boolean).length;

  if (hasPendingChanges && !hasReviewerChanges) {
    return SAVE_UPDATES_LABEL;
  }
  if ((hasPendingChanges && hasReviewerChanges) || reviewerChangeTypesCount > 1 || hasReviewerUpdates) {
    return getUpdateReviewersLabel(selectedReviewersCount);
  }
  if (hasSendToReviewerChanges) {
    return getSendToReviewersLabel(addedReviewersCount + awaitingRequestCount + editRightsGrantedCount);
  }
  if (hasRemovalChanges) {
    return getRemoveReviewersLabel(removedReviewersCount);
  }
  if (hasRevokeEditRightsChanges) {
    return REVOKE_EDIT_RIGHTS_LABEL;
  }

  return NO_UPDATES_LABEL;
}

function getSendToReviewersLabel(reviewersCount: number): string {
  return reviewersCount === 1 ? SEND_TO_REVIEWER_LABEL : SEND_TO_REVIEWERS_LABEL;
}

function getUpdateReviewersLabel(reviewersCount: number): string {
  return reviewersCount === 1 ? UPDATE_REVIEWER_LABEL : UPDATE_REVIEWERS_LABEL;
}

function getRemoveReviewersLabel(reviewersCount: number): string {
  return reviewersCount === 1 ? REMOVE_REVIEWER_LABEL : REMOVE_REVIEWERS_LABEL;
}

/**
 * The reviewers popover's own Send control (#4842). It is not another spelling of the page's
 * label-morphing Save — that button lives in the page header and is deliberately disabled while this
 * popover is open (`isManagingReviewers`), which is exactly why a reviewer added here had no way to be
 * sent without first closing the popover.
 *
 * Send asks exactly one question: has this session ADDED a reviewer who is not with the server yet?
 * The count is diffed against the persisted baseline, so it falls to zero the moment the server
 * acknowledges the addition and Send goes quiet again. Removing a reviewer or revoking edit rights is a
 * roster edit with nobody to send to, so it leaves Send disabled and rides out with Save as it always
 * did.
 *
 * #5134: the host page's `isDirty` used to be a second, independent requirement, which made Send the
 * page Save wearing a different label — an added reviewer restored from the durable staging store after
 * a reload sat behind a permanently disabled button reading "There are no unsaved proposal changes to
 * send", with no way forward but to touch the proposal. Unsaved proposal content no longer gates the
 * control; it decides whether the press is confirmed first (`confirmsUnsavedProposalChanges`), because
 * a Send that also flushes edits the actor did not ask to save is worth one question.
 */
export function getReviewerSendActionState(args: ReviewerSendActionStateArgs): ReviewerSendActionState {
  const { addedReviewersCount, awaitingRequestCount } = getReviewerChangeSummary({
    originalReviewers: args.originalReviewers,
    selectedReviewers: args.selectedReviewers,
  });
  // A reviewer carried on to a revision is already with the server and so is not an addition, but
  // nobody has asked them anything. There is as much to send as for a reviewer added this session.
  const sendableReviewersCount = addedReviewersCount + awaitingRequestCount;
  const hasAddedReviewers = sendableReviewersCount > 0;
  const confirmsUnsavedProposalChanges = hasAddedReviewers && args.proposalHasUnsavedChanges;
  let description = SEND_READY_DESCRIPTION;
  if (!hasAddedReviewers) description = SEND_NEEDS_ADDED_REVIEWER_DESCRIPTION;
  else if (confirmsUnsavedProposalChanges) description = SEND_CONFIRMS_UNSAVED_CHANGES_DESCRIPTION;

  return {
    addedReviewersCount: sendableReviewersCount,
    confirmsUnsavedProposalChanges,
    description,
    disabled: !hasAddedReviewers,
    label: getSendToReviewersLabel(sendableReviewersCount),
  };
}

export function getReviewerActionState(args: ReviewerActionStateArgs): ReviewerActionState {
  const {
    proposalIsModifiedByBuilder,
    proposalHasUnsavedRedlineChanges,
    reviewersAreModified = false,
  } = args;
  const reviewerChanges = getReviewerChangeSummary({
    originalReviewers: args.originalReviewers,
    selectedReviewers: args.selectedReviewers,
  });
  const changedReviewersCount =
    reviewerChanges.changedReviewersCount || (reviewersAreModified && !reviewerChanges.hasReviewerChanges ? 1 : 0);
  const label = getSendButtonLabel({
    addedReviewersCount: reviewerChanges.addedReviewersCount,
    awaitingRequestCount: reviewerChanges.awaitingRequestCount,
    changedReviewersCount,
    editRightsGrantedCount: reviewerChanges.editRightsGrantedCount,
    editRightsRevokedCount: reviewerChanges.editRightsRevokedCount,
    removedReviewersCount: reviewerChanges.removedReviewersCount,
    selectedReviewersCount: reviewerChanges.selectedReviewersCount,
    updatedReviewersCount: reviewerChanges.updatedReviewersCount,
    proposalIsModifiedByBuilder,
    proposalHasUnsavedRedlineChanges,
  });
  const hasReviewerChanges = reviewerChanges.hasReviewerChanges || reviewersAreModified;
  const hasPendingSaveUpdates = proposalIsModifiedByBuilder || proposalHasUnsavedRedlineChanges;
  return {
    ...reviewerChanges,
    changedReviewersCount,
    hasReviewerChanges,
    label,
    disabled: !hasReviewerChanges && !hasPendingSaveUpdates,
    hasPendingSaveUpdates,
    confirmTitle: label === SAVE_UPDATES_LABEL ? "Save Updates?" : `${label}?`,
    confirmMessage: hasPendingSaveUpdates
      ? "This will save all pending changes for review."
      : "This will update the proposal reviewers.",
    confirmSupplementalMessage: undefined,
    confirmOkButtonText: getConfirmOkButtonText(label),
  };
}

function getConfirmOkButtonText(label: string) {
  return CONFIRM_OK_BUTTON_TEXT_BY_LABEL.get(label) ?? "Update";
}
